import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { EnsureSampleSheetResponse } from '@resistapp/common/api-types';
import { LabFeature } from '@resistapp/common/features';
import { Organisation, OrganisationType, User } from '@resistapp/common/types';
import { useEnsureOrganisationSampleSheet } from './api';

type UseLabWorkbookLandingOptions = {
  enabled?: boolean;
};

const organisationTypePriority: Record<OrganisationType, number> = {
  [OrganisationType.CUSTOMER]: 0,
  [OrganisationType.LAB]: 1,
  [OrganisationType.PROVIDER]: 2,
  [OrganisationType.RESISTOMAP]: 3,
};

function hasGeneTemplateAccess(org: Organisation): boolean {
  return Array.isArray(org.labFeatures) && org.labFeatures.includes(LabFeature.GENE_TEMPLATE);
}

export function getPreferredLabWorkbookOrganisationId(user: User | null | undefined): number | null {
  if (!user) {
    return null;
  }
  const eligibleOrganisations = user.organisations.filter(org => !org.isDemo && hasGeneTemplateAccess(org));
  if (eligibleOrganisations.length === 0) {
    return null;
  }
  const sorted = [...eligibleOrganisations].sort((a, b) => {
    const typeDiff = organisationTypePriority[a.type] - organisationTypePriority[b.type];
    if (typeDiff !== 0) {
      return typeDiff;
    }
    return a.id - b.id;
  });
  return sorted[0]?.id ?? null;
}

export function useLabWorkbookLanding(
  user: User | null | undefined,
  options?: UseLabWorkbookLandingOptions,
): {
  organisationId: number | null;
  workbookId: string | null;
  path: string | null;
  ensureQuery: UseQueryResult<EnsureSampleSheetResponse>;
} {
  const organisationId = useMemo(() => getPreferredLabWorkbookOrganisationId(user), [user]);
  const enabled = Boolean(options?.enabled ?? true) && organisationId != null;

  const ensureQuery = useEnsureOrganisationSampleSheet(organisationId, { enabled });
  const workbookId = ensureQuery.data?.labWorkbookId ?? null;
  const path = workbookId ? `/lab/${workbookId}` : null;

  return {
    organisationId,
    workbookId,
    path,
    ensureQuery,
  };
}
